/**
 * 인증 검증 유틸리티
 *
 * 주요 기능:
 * - 토큰 유효성 검사 및 인증 상태 관리
 * - 중앙 집중식 인증 로직 제공
 * - 탭 간 인증 상태 동기화 지원
 * - 비밀번호 유효성 검사
 *
 * 제공 함수:
 * - clearAuthStateAndRedirect: 인증 상태 초기화 및 홈으로 리다이렉트
 * - clearAuthState: 인증 상태만 초기화 (리다이렉트 없음)
 * - validateAuth: 유효한 인증 상태 확인
 * - checkTokenOrClear: 토큰 확인 및 없을 경우 상태 초기화
 * - handleTokenValidationOnFocus: 윈도우 포커스 시 토큰 검증
 * - performPeriodicTokenCheck: 주기적 토큰 검증
 * - validatePassword: 비밀번호 유효성 검사
 *
 * 사용처:
 * - App.tsx: 주기적 토큰 검증 및 포커스 이벤트 처리
 * - API 인터셉터: 토큰 만료 시 인증 상태 초기화
 * - 회원가입/로그인 폼: 비밀번호 유효성 검사
 */

import { useAuthStore } from "@/stores/authStore";
import { tokenService } from "./tokenService";

/**
 * 모든 인증 상태 초기화 및 홈으로 리다이렉트
 *
 * 수행 작업:
 * - 쿠키에서 토큰 제거
 * - 전역 상태에서 사용자 정보 제거
 * - 홈페이지로 리다이렉트 (TanStack Query 캐시도 초기화됨)
 */
export const clearAuthStateAndRedirect = (): void => {
  const { clearUser } = useAuthStore.getState();

  // 토큰 삭제
  tokenService.removeTokens();

  // 인증 상태 초기화
  clearUser();

  // 홈으로 리다이렉트 (모든 TanStack Query 캐시도 초기화됨)
  window.location.href = "/";
};

/**
 * 리다이렉트 없이 인증 상태만 초기화
 *
 * 상태 초기화 후 별도의 네비게이션 처리가 필요한 경우 사용
 * 참고: 페이지 이동/새로고침 시 장바구니는 자동으로 초기화됨
 */
export const clearAuthState = (): void => {
  const { clearUser } = useAuthStore.getState();

  tokenService.removeTokens();
  clearUser();
};

/**
 * 유효한 인증 상태 확인
 *
 * 토큰 존재 여부와 사용자 상태를 모두 확인
 *
 * @returns 유효한 인증 상태 여부
 */
export const validateAuth = (): boolean => {
  const { user } = useAuthStore.getState();
  const hasToken = tokenService.hasAccessToken();

  return hasToken && !!user;
};

/**
 * 토큰 확인 및 없을 경우 인증 상태 초기화
 *
 * @returns 토큰 존재 여부 (토큰 없으면 false 반환 및 상태 초기화)
 */
export const checkTokenOrClear = (): boolean => {
  const { user } = useAuthStore.getState();
  const hasToken = tokenService.hasAccessToken();

  // 토큰이 없지만 사용자 정보가 있는 경우 인증 상태 초기화
  if (!hasToken && user) {
    clearAuthState();
    return false;
  }

  return hasToken;
};

/**
 * 윈도우 포커스 시 토큰 유효성 검증
 *
 * 다른 탭에서 토큰이 제거되었는지 확인하는 데 유용
 */
export const handleTokenValidationOnFocus = (): void => {
  const hasValidToken = checkTokenOrClear();

  if (!hasValidToken) {
    const { user } = useAuthStore.getState();
    if (user) {
      clearAuthStateAndRedirect();
    }
  }
};

/**
 * 주기적 토큰 유효성 검증
 *
 * 일정 간격으로 호출하여 토큰이 제거되지 않았는지 확인
 */
export const performPeriodicTokenCheck = (): void => {
  const { user } = useAuthStore.getState();
  const hasToken = tokenService.hasAccessToken();

  if (!hasToken && user) {
    clearAuthStateAndRedirect();
  }
};

/**
 * 비밀번호 유효성 검사
 *
 * 비밀번호 요구사항:
 * - 최소 8자 이상
 * - 최소 1개의 대문자
 * - 최소 1개의 소문자
 * - 최소 1개의 숫자
 * - 최소 1개의 특수문자
 *
 * @param password - 검사할 비밀번호
 * @returns 오류 메시지 (유효하면 빈 문자열)
 */
export const validatePassword = (password: string): string => {
  if (password.length < 8) {
    return "Password must be at least 8 characters";
  }
  if (!/[A-Z]/.test(password)) {
    return "Include at least one uppercase letter";
  }
  if (!/[a-z]/.test(password)) {
    return "Include at least one lowercase letter";
  }
  if (!/[0-9]/.test(password)) {
    return "Include at least one number";
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return "Include at least one special character";
  }
  return "";
};
